import {
  type DocumentData,
  type DocumentSnapshotModel,
  type GetSnapshotOptions,
  type ModelError,
  type ModelReference,
  type QueryDocumentSnapshotModel,
  type QueryModel,
  type QuerySnapshotModel,
  type SnapshotListenOptions,
  type SnapshotOptions,
} from "@doitintl/models-any";

import { type UseModelsHookOptions } from "../../../react-query";
import { type RefHook, useComparatorRef } from "../../util";
import { type TransformMethod, type TypeOrUndefined } from "../types";

export const snapshotToData = <T extends DocumentData, TTransformModel>(
  snapshot: DocumentSnapshotModel<T> | QueryDocumentSnapshotModel<T>,
  idField: string | undefined,
  refField: string | undefined,
  transform?: TransformMethod<T, TTransformModel>,
  snapshotOptions?: SnapshotOptions
): TTransformModel | null | undefined => {
  const data = snapshot.asModelData(snapshotOptions);

  if (!data) {
    return;
  }

  if (transform) {
    return transform(data, snapshot) as TTransformModel | null | undefined;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    ...(data as any),
    ...(idField ? { [idField]: snapshot.id } : {}),
    ...(refField ? { [refField]: snapshot.ref } : {}),
  } as TTransformModel | null | undefined;
};

interface HasIsEqual {
  isEqual(other: HasIsEqual): boolean;
}

export const isDocRefOrQueryEqual = <T extends HasIsEqual>(v1: TypeOrUndefined<T>, v2: TypeOrUndefined<T>): boolean => {
  const bothNull = !v1 && !v2;

  if (bothNull) {
    return true;
  }

  return !!v1 && !!v2 && v1.isEqual(v2);
};

export const useIsFirestoreRefEqual = <T extends ModelReference<any>>(
  value: TypeOrUndefined<T>,
  onChange?: (value: TypeOrUndefined<T>) => void
): RefHook<TypeOrUndefined<T>> => useComparatorRef(value, isDocRefOrQueryEqual, onChange);

export const useIsFirestoreQueryEqual = <T extends QueryModel<any>>(
  value: TypeOrUndefined<T>,
  onChange?: (value: TypeOrUndefined<T>) => void
): RefHook<TypeOrUndefined<T>> => useComparatorRef(value, isDocRefOrQueryEqual, onChange);

type HandleRefListenBase<TRef, TSnapshot> = {
  ref: TRef | undefined;
  setValue: (snapshot: TSnapshot | undefined) => void;
  setError: (error: ModelError) => void;
};

export function handleRefListen<TRef, TSnapshot>(
  { ref, setValue, setError }: HandleRefListenBase<TRef, TSnapshot>,
  getSnapshot: (ref: TRef, options?: UseModelsHookOptions) => Promise<TSnapshot>,
  onSnapshotWithOptions: (
    ref: TRef,
    options: UseModelsHookOptions,
    setValue: (snapshot: TSnapshot) => void,
    setError: (error: ModelError) => void
  ) => () => void,
  options?: UseModelsHookOptions
) {
  return () => {
    if (!ref) {
      return;
    }

    if (options?.subscribe) {
      const listener = onSnapshotWithOptions(ref, options, setValue, setError);
      return () => {
        listener();
      };
    } else {
      getSnapshot(ref, options)
        .then((snapshot) => {
          setValue(snapshot);
        })
        .catch((err: unknown) => {
          setError(err as ModelError);
        });
    }
  };
}

// Specific implementations for ModelReference
export function handleRefListenDoc<TModel extends DocumentData>(
  params: HandleRefListenBase<ModelReference<TModel>, DocumentSnapshotModel<TModel>>,
  options?: UseModelsHookOptions
) {
  return handleRefListen(
    params,
    (ref, options) => ref.get(options as GetSnapshotOptions),
    (ref, options, setValue, setError) =>
      ref.onSnapshotWithOptions(options as SnapshotListenOptions, setValue, setError),
    options
  );
}

// Specific implementations for QueryModel
export function handleRefListenQuery<TModel extends DocumentData>(
  params: HandleRefListenBase<QueryModel<TModel>, QuerySnapshotModel<TModel>>,
  options?: UseModelsHookOptions
) {
  return handleRefListen(
    params,
    (ref, options) => ref.get(options as GetSnapshotOptions),
    (ref, options, setValue, setError) =>
      ref.onSnapshotWithOptions(options as SnapshotListenOptions, setValue, setError),
    options
  );
}
